import { useState, useEffect, useCallback } from "react";
import {
  MDBContainer,
  MDBTabs,
  MDBTabsContent,
  MDBTabsItem,
  MDBTabsLink,
  MDBTabsPane,
  MDBRow,
  MDBCol,
  MDBBtn,
} from "mdb-react-ui-kit";
import { showErrorToast, fetchManuscriptCounts, exportScreenedManuscripts } from "../../helpers";
import { CountCard } from "../../components";
import UnderReviewManuscriptsTab from "./tabs/UnderReviewManuscriptsTab";
import AmbientScreenedManuscriptsTab from "./tabs/AmbientScreenedManuscriptsTab";
import SearchInput from "../../components/SearchInput/SearchInput";
import { useAppContext } from "../../components/AppContext";
import { useAuth } from "../../components/Auth";
import { useLoader } from "stm-frontend-library";
import {getSelectedJournalIds} from "../../helpers/journals";

const ScreenedManuscripts = () => {
  const { profile } = useAuth();
  const { setSearchInput } = useAppContext();

  const initialTab = profile["app.ambientScreening"] ? "inbox" : "under_review_manuscripts";
  const shouldShowExportAllButton = profile["app.ambientScreening"];

  const [currentTab, setCurrentTab] = useState(initialTab);
  const { showLoader, hideLoader, isLoading } = useLoader();

  const [manuscriptCounts, setManuscriptCounts] = useState({
    duplicateSubmissions: 0,
    ambientScreened: 0,
  });

  const baseTabConfig = [
    {
      key: "under_review_manuscripts",
      label: "Under review",
      component: UnderReviewManuscriptsTab,
    },
    {
      key: "ambient_screened",
      label: "Ambient screened",
      type: "all",
      component: AmbientScreenedManuscriptsTab,
    },
  ];

  const additionalTabConfig = [
    {
      key: "inbox",
      label: "Inbox",
      type: "wo_status",
      showActionColumn: true,
      component: AmbientScreenedManuscriptsTab,
    },
    {
      key: "w_signals",
      label: "With signals",
      type: "w_signals",
      showActionColumn: true,
      component: AmbientScreenedManuscriptsTab,
    },
  ];

  const combinedTabConfig = profile["app.ambientScreening"]
    ? [...additionalTabConfig, ...baseTabConfig]
    : baseTabConfig;

  useEffect(() => {
    const getManuscriptCounts = async () => {
      try {
        showLoader();
        const selectedJournalIds = await getSelectedJournalIds();
        const counts = await fetchManuscriptCounts(selectedJournalIds);
        setManuscriptCounts(counts);
      } catch (error) {
        showErrorToast("Failed to load manuscript counts. Please try again later.");
      } finally {
        hideLoader();
      }
    };

    getManuscriptCounts();
  }, []);

  const handleTabClick = useCallback(
    (tabKey) => {
      if (currentTab !== tabKey) {
        setCurrentTab(tabKey);
      }
    },
    [currentTab]
  );

  const handleCountCardClick = useCallback((tabKey) => {
    setCurrentTab(tabKey);
  }, []);

  const handleExport = async () => {
    try {
      showLoader();
      const isAllExport = !["inbox", "w_signals"].includes(currentTab);
      const { data: fileUrl } = await exportScreenedManuscripts(isAllExport);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      showErrorToast();
    } finally {
      hideLoader();
    }
  };

  return (
    <MDBContainer className="my-5">
      <div className="d-flex justify-content-center m-5">
        <CountCard
          count={manuscriptCounts.duplicateSubmissions}
          title="Manuscripts"
          subtitle="Under Review"
          onClick={() => handleCountCardClick("under_review_manuscripts")}
        />
        <CountCard
          count={manuscriptCounts.ambientScreened}
          title="Manuscripts"
          subtitle="Ambient Screened"
          onClick={() => handleCountCardClick("ambient_screened")}
        />
      </div>

      <MDBRow className="mb-3">
        <MDBCol md="auto" className="ms-4">
          <MDBTabs>
            {combinedTabConfig.map(({ key, label }) => (
              <MDBTabsItem key={key}>
                <MDBTabsLink
                  active={currentTab === key}
                  onClick={() => handleTabClick(key)}
                >
                  {label}
                </MDBTabsLink>
              </MDBTabsItem>
            ))}
          </MDBTabs>
        </MDBCol>
        <MDBCol sm={8} md={6} lg={4} className="ms-auto">
          <SearchInput
            placeholder="Search by manuscript ID or title"
            onSubmit={setSearchInput}
          />
        </MDBCol>
        {shouldShowExportAllButton && (
          <MDBCol md="auto" className="pt-1">
            <MDBBtn
              color="secondary"
              onClick={handleExport}
              disabled={isLoading}
            >
              {["inbox", "w_signals"].includes(currentTab) ? "Export" : "Export all"}
            </MDBBtn>
          </MDBCol>
        )}
      </MDBRow>

      <MDBTabsContent>
        {combinedTabConfig.map(({ key, component: TabComponent, type, showActionColumn }) => (
          <MDBTabsPane key={key} show={currentTab === key}>
            <TabComponent
              isActiveTab={currentTab === key}
              type={type}
              showActionColumn={showActionColumn}
            />
          </MDBTabsPane>
        ))}
      </MDBTabsContent>
    </MDBContainer>
  );
};

export default ScreenedManuscripts;
